import HotelAutocomplete from 'components/ReusableComponents/HotelAutocomplete'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { BEs, parentChains } from 'main/hotelBrandsAndChains'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'

import Dropdown from '../../../components/ReusableComponents/ManageHotel/Dropdown/index'
import FormGroup from '../../../components/ReusableComponents/ManageHotel/FormGroup/index'
import TextInput from '../../../components/ReusableComponents/ManageHotel/TextInput/index'
import { PMSnames } from '../../../main/hotelBrandsAndChains'
import { linkRegex } from '../../../utils/helpers'
import { useBEOptions } from '../../../utils/manageHotel/beHelper'
import styles from './BookingEngine.module.scss'
import SearchEngines from './SearchEngines/index'

const BookingEngine = ({
  formData: {
    hotelName,
    bookingEngineLink,
    externalHotelId,
    bookingEngine,
    pms,
    stateCode,
    parentChainCode,
    numberOfRooms
  },
  handleValidated,
  handleFormChange,
  setFormData,
  disabled,
  done
}) => {
  const { addToast } = useToasts()

  useEffect(() => {
    if (parentChainCode === 'independent') {
      handleValidated(
        !!(
          hotelName &&
          bookingEngineLink &&
          pms &&
          bookingEngine &&
          externalHotelId &&
          stateCode
        )
      )
    } else {
      handleValidated(!!(hotelName && stateCode && externalHotelId))
    }
  }, [
    bookingEngine,
    bookingEngineLink,
    externalHotelId,
    handleValidated,
    hotelName,
    parentChainCode,
    pms,
    stateCode
  ])

  const [beOptions, setBEParent] = useBEOptions()
  const setParent = useCallback(
    parent => {
      setBEParent(parent)
    },
    [setBEParent]
  )

  useEffect(() => {
    setFormData({
      hotelName: '',
      bookingEngineLink: '',
      externalHotelId: '',
      bookingEngine: '',
      pms: ''
    })

    if (parentChainCode === 'choice') {
      setFormData({
        pms: 'choiceadvantage'
      })
    }

    if (parentChainCode === 'hyatt') {
      setFormData({
        pms: 'opera'
      })
    }

    if (parentChainCode === 'ihg') {
      setFormData({
        pms: 'opera'
      })
    }

    if (parentChainCode === 'wyndham') {
      setFormData({
        pms: 'opera'
      })
    }

    if (parentChainCode === 'marriott') {
      setFormData({
        pms: 'disconnected' // fosse
      })
    }

    if (parentChainCode === 'hilton') {
      setFormData({
        pms: 'disconnected' // onq
      })
    }

    setParent(parentChainCode)
  }, [parentChainCode, setFormData, setParent])

  useEffect(() => {
    if (beOptions && Object.keys(beOptions).length === 1) {
      setFormData({ bookingEngine: Object.keys(beOptions)[0] })
      return
    }

    if (beOptions && !Object.keys(beOptions).includes(bookingEngine)) {
      setFormData({ bookingEngine: null })
    }
  }, [beOptions, bookingEngine, setFormData])

  useEffect(() => {
    if (PMSnames && Object.keys(PMSnames).length === 1) {
      setFormData({ pms: Object.keys(PMSnames)[0] })
      return
    }

    if (PMSnames && pms && !Object.keys(PMSnames).includes(pms)) {
      setFormData({ pms: null })
    }
  }, [pms, setFormData])

  function handleHotelAddress({
    hotelName,
    city,
    stateCode,
    countryCode,
    postalCode,
    streetAddress,
    placesId
  }) {
    const fullHotelName = !hotelName.includes(city) ? `${hotelName} ${city}` : hotelName

    setFormData({
      hotelName: fullHotelName,
      city,
      stateCode,
      countryCode,
      postalCode,
      streetAddress,
      placesId
    })
  }

  const allStates = { ...usStates, ...canadianProvinces }

  const [addHotelNameManually, setAddHotelNameManually] = useState(false)

  useEffect(() => {
    setFormData({
      hotelName: '',
      city: '',
      stateCode: '',
      countryCode: '',
      postalCode: '',
      streetAddress: '',
      placesId: ''
    })
  }, [addHotelNameManually, setFormData])

  function handleIndieBEurl(e) {
    const value = e.target.value

    if (value.includes('{hotelExternalId}')) return

    if (value.includes('reztrip.com')) {
      setFormData({
        bookingEngine: 'reztrip',
        externalHotelId: value.match(/https?:\/\/(.+)\.reztrip\.com/)?.[1] || ''
      })
    }

    if (value.includes('bestwestern.com')) {
      setFormData({
        bookingEngine: 'bestwestern',
        externalHotelId: value.match(/.+.\/book\/.+\.(\d{5})/)?.[1] || '',
        pms: 'opera'
      })
    }

    if (
      value.includes('#/accommodation') ||
      value.includes('#/datesofstay') ||
      value.includes('#/guestsandrooms')
    ) {
      const match = value.match(
        /https:\/\/([a-zA-Z.]+)\/(\d+)(?:.+)?(#\/accommodation\/room|#\/datesofstay|#\/guestsandrooms)/
      )

      const bookingEngineSlug = match?.[1]
      const externalHotelId = match?.[2]

      setFormData({
        bookingEngine: 'travelclick',
        externalHotelId,
        bookingEngineSlug
      })
    }

    if (value.includes('pegsbe.com/rooms')) {
      setFormData({
        bookingEngine: 'pegasus',
        externalHotelId: value.match(/(?<=https:\/\/)[a-z0-9]+/)[0],
        pms: 'skytouch'
      })
    }

    if (value.includes('secure.guestcentric.net')) {
      setFormData({
        bookingEngine: 'guestcentric',
        externalHotelId: value.match(/apikey=([^&]+)/)[1]
      })
    }

    if (value.includes('direct-book.com')) {
      setFormData({
        bookingEngine: 'direct-book',
        externalHotelId: value.match(/direct-book\.com.+properties\/(.+)\?/)?.[1] || '',
        bookingEngineSlug: value.match(/direct-book\.com.+properties\/(.+)\?/)?.[1] || ''
      })
    }

    if (value.includes('redroof.com')) {
      setFormData({
        bookingEngine: 'redroof',
        externalHotelId: value.match(/redroof.com\/.+DestId=(RR[A-Z0-9]+)/)[1]
      })
    }

    if (value.includes('stayntouch.website')) {
      setFormData({
        bookingEngine: BEs.stayntouch,
        externalHotelId: value.match(/(\w+)\.stayntouch\.website/)[1]
      })
    }

    if (value.includes('/premium/index') && value.includes('id_albergo')) {
      const urlParams = new URLSearchParams(value.split('?')[1])

      const bookingEngineSlug = `&dc=${urlParams.get('dc')}`
      const externalHotelId = urlParams.get('id_albergo')

      setFormData({
        bookingEngine: BEs.visualmatrix,
        externalHotelId: externalHotelId,
        bookingEngineSlug: bookingEngineSlug
      })
    }

    if (value.includes('book.sonesta.com')) {
      addToast('Please use sonesta.com link on the home page of the hotel', {
        appearance: 'error'
      })
    }

    if (value.includes('sonesta.com') && !value.includes('book.sonesta.com')) {
      const params = value.match(/sonesta\.com\/([a-z-]+\/[a-z-]{2}\/[a-z-]+\/([a-z-]+))/)
      setFormData({
        bookingEngine: BEs.sonesta,
        bookingEngineSlug: params[1],
        externalHotelId: params[2]
      })
    }

    const search = value.match(/be\.synxis\.com\/\?(.+)/)?.[1]

    if (search) {
      const urlParams = new URLSearchParams(search)

      setFormData({
        externalHotelId: urlParams.get('hotel'),
        bookingEngine: BEs.synxis,
        bookingEngineSlug: `be.synxis.com/?chain=${urlParams.get('chain')}`,
        bookingEngineLink: `https://be.synxis.com/?adult=1&rooms=1&chain=${urlParams.get(
          'chain'
        )}&hotel={hotelExternalId}&arrive={checkIn}&depart={checkOut}&level=hotel&start=availresults`
      })
      //return
    }
  }

  return (
    <FormGroup title="Booking engine" done={done} disabled={disabled}>
      <div className={styles.container}>
        <div>
          {addHotelNameManually ? (
            <>
              <TextInput
                label="Hotel name"
                value={hotelName}
                name="hotelName"
                onChange={handleFormChange}
                style={{ width: 320 }}
                valid={hotelName && !!hotelName}
                type="search"
              />
              <Dropdown
                options={allStates}
                label="State"
                defaultVal="Select state"
                name="stateCode"
                value={stateCode}
                onChange={handleFormChange}
                valid={!!stateCode}
              />
            </>
          ) : (
            <label data-testid="hotelNameAndAddress">
              <span className={styles.spanLabel}>Hotel name &amp; address</span>
              <HotelAutocomplete onChange={handleHotelAddress} />
            </label>
          )}
          <br />
          <Toggle
            className={styles.toggle}
            label="Add manually"
            checked={addHotelNameManually}
            handleChange={e => setAddHotelNameManually(e.target.checked)}
          />
        </div>

        <br />
        {parentChainCode === 'independent' ? (
          <TextInput
            label="Booking engine URL"
            style={{ width: 400 }}
            onChange={handleFormChange}
            onBlur={handleIndieBEurl}
            name="bookingEngineLink"
            value={bookingEngineLink}
          ></TextInput>
        ) : (
          <>
            {parentChainCode !== parentChains.sonesta && (
              <SearchEngines
                label="Booking engine URL"
                style={{ width: 400 }}
                value={bookingEngineLink}
                setFormData={setFormData}
                parentChainCode={parentChainCode}
                hotelName={hotelName}
                valid={
                  bookingEngineLink && (bookingEngineLink.match(linkRegex) ? true : false)
                }
                readOnly
              />
            )}
            {parentChainCode === parentChains.sonesta && (
              <TextInput
                label="Booking engine URL"
                style={{ width: 400 }}
                onChange={handleFormChange}
                onBlur={handleIndieBEurl}
                name="bookingEngineLink"
                value={bookingEngineLink}
              ></TextInput>
            )}
          </>
        )}

        <br />
        <br />
        <TextInput
          label="Hotel external ID"
          value={externalHotelId}
          onChange={handleFormChange}
          name="externalHotelId"
          valid={externalHotelId && !!externalHotelId}
        />
        <Dropdown
          options={beOptions}
          label="Booking engine"
          name="bookingEngine"
          onChange={handleFormChange}
          defaultVal="Select booking engine"
          value={bookingEngine}
          valid={bookingEngine && !!bookingEngine}
        />
        <Dropdown
          options={PMSnames}
          label="PMS"
          defaultVal="Select PMS"
          name="pms"
          onChange={handleFormChange}
          value={pms}
          valid={pms && !!pms}
        />
        <br />
        <TextInput
          type="number"
          label="Number of rooms"
          className={styles.input}
          name="numberOfRooms"
          value={numberOfRooms}
          onChange={e => setFormData({ numberOfRooms: Number(e.target.value) })}
        />
      </div>
    </FormGroup>
  )
}

BookingEngine.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  handleValidated: PropTypes.func,
  disabled: PropTypes.bool,
  done: PropTypes.bool
}

export default BookingEngine
